import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { propertyFormSchema } from '@/utils/validationSchemas';
import { PropertyFormData, PropertyType, ListingType, Property } from '@/types';

import { StringArrayInput } from './StringArrayInput';

interface PropertyFormProps {
  property?: Property;
  onSubmit: (data: PropertyFormData, imageFiles?: File[], documentFiles?: File[]) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
}

const steps = ['Basic Information', 'Property Details', 'Location', 'Features', 'Review'];

const DRAFT_KEY = 'property_form_draft';

const PropertyForm: React.FC<PropertyFormProps> = ({
  property,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [draftRestored, setDraftRestored] = useState(false);
  
  // Separate existing images from new uploads to handle deletion correctly
  const [existingImages, setExistingImages] = useState<string[]>(property?.metadata.images || []);
  const [newImages, setNewImages] = useState<{ file: File; preview: string }[]>([]);
  
  // Same for documents
  const [existingDocuments, setExistingDocuments] = useState<string[]>(property?.metadata.documents || []);
  const [newDocuments, setNewDocuments] = useState<File[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [newUtility, setNewUtility] = useState('');
  const [newAmenity, setNewAmenity] = useState('');
  const [newTag, setNewTag] = useState('');

  const getInitialValues = (): PropertyFormData => {
    if (property) {
      return {
        metadata: {
          title: property.metadata.title,
          description: property.metadata.description,
          price: property.metadata.price,
          currency: property.metadata.currency,
          propertyType: property.metadata.propertyType,
          listingType: property.metadata.listingType,
          bedrooms: property.metadata.bedrooms,
          bathrooms: property.metadata.bathrooms,
          squareFootage: property.metadata.squareFootage,
          lotSize: property.metadata.lotSize,
          yearBuilt: property.metadata.yearBuilt,
          parkingSpaces: property.metadata.parkingSpaces,
          petFriendly: property.metadata.petFriendly,
          furnished: property.metadata.furnished,
          utilities: property.metadata.utilities || [],
          amenities: property.metadata.amenities || [],
          virtualTour: property.metadata.virtualTour,
          videoUrl: property.metadata.videoUrl,
        },
        features: property.features,
        location: property.location,
        tags: property.tags,
        isFeatured: property.isFeatured,
        isActive: property.isActive,
        listingDate: (property.listingDate as any).toDate ? (property.listingDate as any).toDate() : new Date(property.listingDate as any),
        expirationDate: property.expirationDate ? ((property.expirationDate as any).toDate ? (property.expirationDate as any).toDate() : new Date(property.expirationDate as any)) : undefined,
      } as PropertyFormData;
    }

    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        // Ensure dates are parsed correctly
        if (parsed.listingDate) parsed.listingDate = new Date(parsed.listingDate);
        if (parsed.expirationDate) parsed.expirationDate = new Date(parsed.expirationDate);
        return parsed;
      }
    } catch (e) {
      console.error('Failed to parse draft', e);
    }

    return {
      metadata: {
        title: '',
        description: '',
        price: 0,
        currency: 'USD',
        propertyType: PropertyType.HOUSE,
        listingType: ListingType.SALE,
        bedrooms: 0,
        bathrooms: 0,
        squareFootage: 0,
        lotSize: undefined,
        yearBuilt: undefined,
        parkingSpaces: undefined,
        petFriendly: false,
        furnished: false,
        utilities: [],
        amenities: [],
        virtualTour: undefined,
        videoUrl: undefined,
      },
      features: {
        heating: [],
        cooling: [],
        flooring: [],
        appliances: [],
        security: [],
        outdoor: [],
        accessibility: [],
        energyEfficient: false,
        smartHome: false,
        pool: false,
        garden: false,
        balcony: false,
        fireplace: false,
        basement: false,
        attic: false,
        garage: false,
      },
      location: {
        latitude: 0,
        longitude: 0,
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        neighborhood: undefined,
        schoolDistrict: undefined,
        walkScore: undefined,
        transitScore: undefined,
      },
      tags: [],
      isFeatured: false,
      isActive: true,
      listingDate: new Date(),
      expirationDate: undefined,
    } as PropertyFormData;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PropertyFormData>({
    resolver: yupResolver(propertyFormSchema),
    defaultValues: getInitialValues(),
  });

  // Watch form values for auto-save
  const formValues = watch();

  useEffect(() => {
    if (!property) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
      }, 1500); // Debounce save
      return () => clearTimeout(timeoutId);
    }
  }, [formValues, property]);

  useEffect(() => {
    if (!property && localStorage.getItem(DRAFT_KEY)) {
      setDraftRestored(true);
    }
  }, [property]);

  const handleClearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftRestored(false);
    window.location.reload(); // Simple way to reset form
  };

  const { fields: utilityFields, append: appendUtility, remove: removeUtility } = useFieldArray({
    control,
    name: 'metadata.utilities' as never,
  });

  const { fields: amenityFields, append: appendAmenity, remove: removeAmenity } = useFieldArray({
    control,
    name: 'metadata.amenities' as never,
  });

  const { fields: tagFields, append: appendTag, remove: removeTag } = useFieldArray({
    control,
    name: 'tags' as never,
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const newImageEntries = newFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setNewImages((prev) => [...prev, ...newImageEntries]);
  };

  const handleRemoveImage = (index: number) => {
    if (index < existingImages.length) {
      // Remove from existing images
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remove from new images
      const newIndex = index - existingImages.length;
      setNewImages((prev) => prev.filter((_, i) => i !== newIndex));
    }
  };

  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    setNewDocuments((prev) => [...prev, ...newFiles]);
  };

  const handleRemoveDocument = (index: number) => {
    if (index < existingDocuments.length) {
      setExistingDocuments((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingDocuments.length;
      setNewDocuments((prev) => prev.filter((_, i) => i !== newIndex));
    }
  };

  const handleNext = async () => {
    const isValid = await handleStepValidation();
    if (isValid) {
      setActiveStep((prev) => prev + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleStepValidation = async (): Promise<boolean> => {
    try {
      // Ideally we would validate only fields in the current step
      // But for now we'll just do a simple check or rely on required fields
      return true; 
    } catch (error: any) {
      setError(error.message || 'Please fill in all required fields');
      return false;
    }
  };

  const onFormSubmit = async (data: PropertyFormData) => {
    try {
      setError(null);
      
      // Prepare the final data object
      // We override the images and documents arrays with our managed state
      // For new uploads, we pass the files separately to the parent
      
      const finalData: PropertyFormData = {
        ...data,
        metadata: {
          ...data.metadata,
          images: existingImages, // Only include existing images (URLs) in the data
          documents: existingDocuments,
        }
      };
      
      const imageFilesToUpload = newImages.map(img => img.file);
      const documentFilesToUpload = newDocuments;

      await onSubmit(finalData, imageFilesToUpload, documentFilesToUpload);
      
      // Clear draft on successful submit
      if (!property) {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save property');
    }
  };

  const handleSaveDraft = () => {
      setValue('isActive', false);
      handleSubmit(onFormSubmit)();
  };

  const handleAddUtility = () => {
    if (newUtility.trim()) {
      appendUtility(newUtility.trim() as any); 
      setNewUtility('');
    }
  };

  const handleAddAmenity = () => {
    if (newAmenity.trim()) {
      appendAmenity(newAmenity.trim() as any);
      setNewAmenity('');
    }
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      appendTag(newTag.trim() as any);
      setNewTag('');
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      newImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [newImages]);

  // Combined list for display
  const displayImages = [...existingImages, ...newImages.map(img => img.preview)];
  const displayDocuments = [...existingDocuments, ...newDocuments.map(doc => doc.name)];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="metadata.title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Property Title"
                    fullWidth
                    required
                    error={!!errors.metadata?.title}
                    helperText={errors.metadata?.title?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="metadata.description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    error={!!errors.metadata?.description}
                    helperText={errors.metadata?.description?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.propertyType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Property Type</InputLabel>
                    <Select {...field} label="Property Type" error={!!errors.metadata?.propertyType}>
                      {Object.values(PropertyType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.listingType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Listing Type</InputLabel>
                    <Select {...field} label="Listing Type" error={!!errors.metadata?.listingType}>
                      {Object.values(ListingType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Price"
                    fullWidth
                    required
                    error={!!errors.metadata?.price}
                    helperText={errors.metadata?.price?.message}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.currency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required>
                    <InputLabel>Currency</InputLabel>
                    <Select {...field} label="Currency">
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="EUR">EUR</MenuItem>
                      <MenuItem value="GBP">GBP</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.bedrooms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Bedrooms"
                    fullWidth
                    required
                    error={!!errors.metadata?.bedrooms}
                    helperText={errors.metadata?.bedrooms?.message}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.bathrooms"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Bathrooms"
                    fullWidth
                    required
                    error={!!errors.metadata?.bathrooms}
                    helperText={errors.metadata?.bathrooms?.message}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.squareFootage"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Square Footage"
                    fullWidth
                    required
                    error={!!errors.metadata?.squareFootage}
                    helperText={errors.metadata?.squareFootage?.message}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.lotSize"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Lot Size (sq ft)"
                    fullWidth
                    error={!!errors.metadata?.lotSize}
                    helperText={errors.metadata?.lotSize?.message}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.yearBuilt"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Year Built"
                    fullWidth
                    error={!!errors.metadata?.yearBuilt}
                    helperText={errors.metadata?.yearBuilt?.message}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="metadata.parkingSpaces"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Parking Spaces"
                    fullWidth
                    error={!!errors.metadata?.parkingSpaces}
                    helperText={errors.metadata?.parkingSpaces?.message}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Utilities</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                        size="small"
                        label="Add Utility"
                        value={newUtility}
                        onChange={(e) => setNewUtility(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddUtility();
                            }
                        }}
                    />
                    <IconButton onClick={handleAddUtility} color="primary">
                        <AddIcon />
                    </IconButton>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {utilityFields.map((field, index) => (
                        <Chip
                            key={field.id}
                            label={watch(`metadata.utilities.${index}`)}
                            onDelete={() => removeUtility(index)}
                        />
                    ))}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Amenities</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                        size="small"
                        label="Add Amenity"
                        value={newAmenity}
                        onChange={(e) => setNewAmenity(e.target.value)}
                         onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddAmenity();
                            }
                        }}
                    />
                    <IconButton onClick={handleAddAmenity} color="primary">
                        <AddIcon />
                    </IconButton>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {amenityFields.map((field, index) => (
                        <Chip
                            key={field.id}
                            label={watch(`metadata.amenities.${index}`)}
                            onDelete={() => removeAmenity(index)}
                        />
                    ))}
                </Box>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
             <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                  Note: Address auto-complete will be integrated with Google Maps API.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="location.address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Street Address"
                    fullWidth
                    required
                    error={!!errors.location?.address}
                    helperText={errors.location?.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="location.city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    fullWidth
                    required
                    error={!!errors.location?.city}
                    helperText={errors.location?.city?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="location.state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="State"
                    fullWidth
                    required
                    error={!!errors.location?.state}
                    helperText={errors.location?.state?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="location.zipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Zip Code"
                    fullWidth
                    required
                    error={!!errors.location?.zipCode}
                    helperText={errors.location?.zipCode?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="location.latitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Latitude"
                    fullWidth
                    required
                    error={!!errors.location?.latitude}
                    helperText={errors.location?.latitude?.message}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="location.longitude"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Longitude"
                    fullWidth
                    required
                    error={!!errors.location?.longitude}
                    helperText={errors.location?.longitude?.message}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="location.neighborhood"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Neighborhood"
                    fullWidth
                    error={!!errors.location?.neighborhood}
                    helperText={errors.location?.neighborhood?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="location.schoolDistrict"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="School District"
                    fullWidth
                    error={!!errors.location?.schoolDistrict}
                    helperText={errors.location?.schoolDistrict?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="features.pool"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Pool"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="features.garage"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Garage"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="features.garden"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Garden"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="features.fireplace"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Fireplace"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.petFriendly"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Pet Friendly"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="metadata.furnished"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Furnished"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Detailed Features
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.heating" label="Heating" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.cooling" label="Cooling" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.flooring" label="Flooring" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.appliances" label="Appliances" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.security" label="Security" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.outdoor" label="Outdoor Features" />
            </Grid>
            <Grid item xs={12} md={6}>
              <StringArrayInput control={control} name="features.accessibility" label="Accessibility" />
            </Grid>

             <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Tags</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                        size="small"
                        label="Add Tag"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                         onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                            }
                        }}
                    />
                    <IconButton onClick={handleAddTag} color="primary">
                        <AddIcon />
                    </IconButton>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {tagFields.map((field, index) => (
                        <Chip
                            key={field.id}
                            label={watch(`tags.${index}`)}
                            onDelete={() => removeTag(index)}
                        />
                    ))}
                </Box>
            </Grid>
             <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Images
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                multiple
                onChange={handleImageUpload}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Images
                </Button>
              </label>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {displayImages.map((image, index) => (
                  <Box key={index} sx={{ position: 'relative' }}>
                    <img
                      src={image}
                      alt={`Preview ${index + 1}`}
                      style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Documents
              </Typography>
              <input
                accept=".pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                id="document-upload"
                type="file"
                multiple
                onChange={handleDocumentUpload}
              />
              <label htmlFor="document-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Documents
                </Button>
              </label>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                {displayDocuments.map((docName, index) => (
                  <Chip
                    key={index}
                    label={docName}
                    onDelete={() => handleRemoveDocument(index)}
                    icon={<InsertDriveFileIcon />}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="isFeatured"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Featured Property"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Active Listing"
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Property Information
            </Typography>
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="body1">
                <strong>Title:</strong> {watch('metadata.title')}
              </Typography>
              <Typography variant="body1">
                <strong>Type:</strong> {watch('metadata.propertyType')} - {watch('metadata.listingType')}
              </Typography>
              <Typography variant="body1">
                <strong>Price:</strong> {watch('metadata.currency')} {watch('metadata.price').toLocaleString()}
              </Typography>
              <Typography variant="body1">
                <strong>Location:</strong> {watch('location.address')}, {watch('location.city')}, {watch('location.state')}
              </Typography>
               <Typography variant="body1">
                <strong>Bedrooms:</strong> {watch('metadata.bedrooms')} | <strong>Bathrooms:</strong> {watch('metadata.bathrooms')}
              </Typography>
               <Typography variant="body1">
                <strong>Images:</strong> {displayImages.length} ({newImages.length} new)
              </Typography>
              <Typography variant="body1">
                <strong>Documents:</strong> {displayDocuments.length} ({newDocuments.length} new)
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {draftRestored && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearDraft}>
              Clear Draft
            </Button>
          }
        >
          A saved draft was restored. You can continue editing or clear it to start fresh.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onFormSubmit)}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <>
                 <Button 
                    onClick={handleSaveDraft} 
                    variant="outlined"
                    sx={{ mr: 2 }}
                    disabled={loading}
                    startIcon={<SaveIcon />}
                 >
                    Save Draft
                 </Button>
                {onCancel && (
                  <Button onClick={onCancel} sx={{ mr: 2 }}>
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Saving...' : property ? 'Update Property' : 'Publish Property'}
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default PropertyForm;
